import axios from '../../services/session/apiClient'
import { presignDownload } from '../../services/session/SessionApi'
import qs from "qs";


/**
 * @typedef {Object} SentimentQuery
 * @param {string[]} [entities]
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @param {string[]} [providers]
 * @param {string} [keyword] - text search on segments
 * @param {string} [searchQuery] - alias for keyword
 * @param {string[]} [topics] - filter to match one of the topic words
 * @param {string[]} [categories] - filter to match one of the category ids
 * @param {string[]} [attributes] - filter to match one of the attribute words
 * @param {string[]} [sentiments] - filter to match one of the sentiment names
 */

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const groupComparisonReport = (params) => {
  return axios.get('sentiment/groupComparison', { params })
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const storeComparisonReport = (params) => {
  return axios.get('sentiment/storeComparison', { params })
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const exportStoreComparisonReport = (params) => {

  const paramz = qs.stringify(params, { arrayFormat: 'repeat' });

  return presignDownload(`/sentiment/exportStoreComparison?${paramz}`)
    .then((resp) => {
      window.location = resp.data.url + `&bewit=${resp.data.token}`;
    })
}


export function getTopicSet(setId) {
  return axios.get(`/sentiment/trackedTopics/${setId}`).then(x => x.data)
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const getContentAnalysisStats = (params) => {
  return axios.get('/v5/sentiment/contentAnalysisStats', {
    params: {
      // Keep in sync with email alerts. `PulseGadgetInfuser.scala`
      minCategoryRatio: 6 / 360,
      minTopicRatio: 1 / 1000,
      minAttributeRatio: 1 / 7200,
      ...params
    }
  });
}

export const getContentAnalysisPercentChange = (searchConfig, data) => {
  return axios.post('/v5/sentiment/path/stats', null, {
    params: searchConfig,
    data: {
      paths: data
    }
  })
    .then(resp => {
      return resp.data;
    });
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */

export const getSentimentTimeline = (params) => {

  return axios.get('/v5/sentiment/sentimentTimeline', { params });
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<PointOfInterestDatum[]>}
 */
export const getSentimentPointsOfInterest = (params) => {
  return axios.get('/v5/sentiment/sentimentPointsOfInterest', { params }).then(r => r.data.pointsOfInterest);
}


/**
 * @typedef {Object} PointOfInterestDatum
 *
 * @property {Date | string} from
 * @property {Date | string} to
 * @property {PointOfInterestSentimentStats} stats
 * @property {PointOfInterestAbout} about
 */

/**
 * @typedef {Object} PointOfInterestAbout
 * @property {PointOfInterestTopic[]} topics
 * @property {boolean} trendBased
 * @property {number} trendDirection
 * @property {number} percentChangeOfReviewVolume
 * @property {number} percentChangeOfAvgSentiment
 */

/**
 * @typedef {Object} PointOfInterestSentimentStats
 * @property {string} from
 * @property {string} to
 * @property {[]} sentimentHistogram
 * @property {number} count:
 * @property {number} avg?:
 * @property {number} sum?:
 * @property {number} variance?:
 * @property {number} stdDeviation?:
 * @property {number} reviewCount?:
 */

/**
 * @typedef {Object} PointOfInterestTopic
 *
 * @property {string} term - topic word
 * @property {number} percentChange - [-100, 100]
 * @property {number} mentions - [0, ∞)
 * @property {number} previousMentions - [0, ∞)
 * @property {number} currentSentiment - [1, 5]
 */

/**
 *
 * @param {Date} analysisStartDate
 * @param {Date} analysisEndDate
 * @param {SentimentQuery} params
 * @return {Promise.<PointOfInterestDatum | null>}
 */
export const getPointOfInterestDetails = ({
  analysisStartDate,
  analysisEndDate,
  ...params
}) => {
  return axios.get('/v5/sentiment/pointOfInterestAnalysis', {params: {...params, analysisStartDate, analysisEndDate}})
    .then(response => response.data.pointOfInterest)
};


/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const getCategories = (params) => {
  return axios.get('/sentiment/categories', { params }).then((data) => data.data);
}

/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const getTopics = (params) => {
  return axios.get('/sentiment/topics', { params }).then((data) => data.data);
}




export function getTrackedTopicSets({ accountId = undefined } = {}) {
  return axios.get('/sentiment/trackedTopicSets', {
    params: {
      accountId
    }
  })
    .then(resp => resp.data['trackedTopicSets'])
}


/**
 *
 * @param {SentimentQuery} params
 * @return {Promise.<*>}
 */
export const getSegments = (params) => {
  return axios.get('/sentiment/segments', { params })
    .then(resp => {
      return resp.data;
    })
}

/**
 *
 * @param {string[]} reviewIds
 * @return {Promise.<*>}
 */
export const getReviewTopics = ({ reviewIds }) => {
  return axios.post('/sentiment/reviewTopics', { reviewIds })
    .then(resp => {
      return resp.data;
    })
}


export const specifySegmentSentiment = (segmentId, polarity) => {
  return axios.put(`/v5/sentiment/specifySegmentSentiment/${segmentId}`, { polarity: polarity })
    .then(resp => {
      return resp.data;
    })
}